import { useListPagination } from '@/hooks/useListPagination'
import { ListPagination } from '@/components/ui/list-pagination'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useSortingState } from '@/hooks/useSortingState'
import { SortableTableHead } from '@/components/ui/sortable-table-head'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { Package } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { configurazioniApi } from '@/api/box'

/**
 * Pagina lista configurazioni scatola.
 * Mostra tutte le configurazioni (combinazioni di componenti).
 */
export default function ConfigurazioniList() {
  const [search, setSearch] = useState('')
  const [filtroAttivo, setFiltroAttivo] = useState('tutti')
  const [ordering, setOrdering] = useSortingState('box-configurazioni', 'nome')
  const [deleteDialog, setDeleteDialog] = useState({ open: false, configurazione: null })
  
  const queryClient = useQueryClient()

  // Query configurazioni
  const { page, setPage } = useListPagination({ search, filtroAttivo, ordering })

  const { data: configurazioniData, isLoading, isFetching, error } = useQuery({
    queryKey: ['configurazioni-scatola', { page, search, attivo: filtroAttivo, ordering }],
    queryFn: () => {
      const params = { ordering, page }
      if (search) params.search = search
      if (filtroAttivo !== 'tutti') params.attivo = filtroAttivo === 'attive'
      return configurazioniApi.getAll(params)
    },
  })

  // Mutation eliminazione
  const deleteMutation = useMutation({
    mutationFn: configurazioniApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configurazioni-scatola'] })
      toast.success('Configurazione eliminata con successo')
      setDeleteDialog({ open: false, configurazione: null })
    },
    onError: () => toast.error('Errore durante l\'eliminazione'),
  })

  const configurazioni = Array.isArray(configurazioniData?.data)
    ? configurazioniData.data
    : (configurazioniData?.data?.results || [])

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Configurazioni Scatola</h1>
          <p className="text-muted-foreground">
            Gestisci le combinazioni di componenti per le scatole
          </p>
        </div>
        <Button asChild>
          <Link to="/box/configurazioni/nuovo">
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuova Configurazione
          </Link>
        </Button>
      </div>

      {/* Statistiche */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Totale Configurazioni
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{configurazioni.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Attive
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {configurazioni.filter(c => c.attivo).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Costo Medio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {configurazioni.length > 0
                ? formatCurrency(
                    configurazioni.reduce((acc, c) => acc + (c.costo_totale_cents || 0), 0) /
                    configurazioni.length
                  )
                : '€0,00'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtri */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filtri</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Cerca per nome o codice..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filtroAttivo} onValueChange={setFiltroAttivo}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti</SelectItem>
                <SelectItem value="attive">Attive</SelectItem>
                <SelectItem value="inattive">Inattive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabella */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : configurazioni.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessuna configurazione trovata</p>
              <Button asChild className="mt-4" variant="outline">
                <Link to="/box/configurazioni/nuovo">Crea la prima configurazione</Link>
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead field="codice" currentOrdering={ordering} onSort={setOrdering}>
                    Codice
                  </SortableTableHead>
                  <SortableTableHead field="nome" currentOrdering={ordering} onSort={setOrdering}>
                    Nome
                  </SortableTableHead>
                  <SortableTableHead field="num_componenti" currentOrdering={ordering} onSort={setOrdering} className="text-center">N. Componenti</SortableTableHead>
                  <SortableTableHead field="costo_totale_cents" currentOrdering={ordering} onSort={setOrdering} className="text-right">Costo Totale</SortableTableHead>
                  <SortableTableHead field="attivo" currentOrdering={ordering} onSort={setOrdering} className="text-center">Stato</SortableTableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configurazioni.map((config) => (
                  <TableRow key={config.id}>
                    <TableCell className="font-mono text-sm">
                      <Link to={`/box/configurazioni/${config.id}/modifica`} className="hover:underline">
                        {config.codice}
                      </Link>
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link to={`/box/configurazioni/${config.id}/modifica`} className="hover:underline">
                        {config.nome}
                      </Link>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{config.num_componenti || 0}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(config.costo_totale_cents || 0)}
                    </TableCell>
                    <TableCell className="text-center">
                      {config.attivo ? (
                        <Badge variant="success">Attiva</Badge>
                      ) : (
                        <Badge variant="secondary">Inattiva</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" title="Modifica configurazione" aria-label="Modifica configurazione" asChild>
                          <Link to={`/box/configurazioni/${config.id}/modifica`}>
                            <PencilSquareIcon className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          title="Elimina configurazione"
                          aria-label="Elimina configurazione"
                          onClick={() => setDeleteDialog({ open: true, configurazione: config })}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog Conferma Eliminazione */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, configurazione: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare la configurazione &quot;{deleteDialog.configurazione?.nome}&quot;?
              Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, configurazione: null })}>
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate(deleteDialog.configurazione?.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Eliminazione...' : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ListPagination data={configurazioniData?.data} page={page} setPage={setPage} isFetching={isFetching} error={error} />
    </div>
  )
}
